import React from "react";
import {
  createOwnProfessional, getCircle, listOwnProfessionals, newOperationId,
  removeMember, replaceMember, searchNetwork, selectMember, transitionMember,
} from "@/lib/vg2PaNetwork";

/**
 * Professionisti e Circle — PA Case section (Phase 9).
 *
 * Bounded UI (§36): confirmed role needs → source choice → search
 * or own-network add → explicit selection → Circle membership
 * lifecycle. No provider marketplace, no ratings, no automated
 * outreach.
 */
export default function PaCircleSection({ paId, caseId }) {
  const [view, setView] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [own, setOwn] = React.useState({ items: [] });
  const [drawer, setDrawer] = React.useState(null); // {kind, roleNeed}

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const [c, o] = await Promise.all([
        getCircle({ paId, caseId }),
        listOwnProfessionals({ paId, caseId }),
      ]);
      setView(c); setOwn(o);
    } catch (e) { setError(e); }
  }, [paId, caseId]);

  React.useEffect(() => { load(); }, [load]);

  if (error) return (
    <div className="mb-6 border border-vg2-line rounded p-4"
         data-testid="pa-circle-error">
      <p className="text-sm text-vg2-ink">
        Errore Circle: {error.message}
      </p>
    </div>
  );
  if (!view) return (
    <p className="text-sm text-vg2-muted"
       data-testid="pa-circle-loading">Caricamento Circle…</p>
  );

  return (
    <section id="pa-section-CIRCLE" className="mb-8"
             data-testid="pa-circle-root">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-lg font-medium">
          Professionisti e Circle
        </h2>
        <span className={`text-xs px-2 py-1 rounded ${
          view.circle_status === "BLOCKED"
            ? "bg-vg2-red text-white"
            : "bg-vg2-subtle text-vg2-ink"
        }`}
              data-testid="pa-circle-status">
          {view.circle_status_label}
        </span>
      </div>

      {view.role_needs_review_required && (
        <div className="mb-3 border border-vg2-red/40 rounded p-3 text-sm text-vg2-ink"
             data-testid="pa-circle-review">
          Il piano d'azione è cambiato. Rivedi manualmente i ruoli
          prima di procedere.
          {view.new_needs.length > 0 && (
            <p className="text-xs mt-1">
              Nuovi ruoli: {view.new_needs.join(", ")}
            </p>
          )}
          {view.removed_needs.length > 0 && (
            <p className="text-xs">
              Ruoli rimossi: {view.removed_needs.join(", ")}
            </p>
          )}
        </div>
      )}

      {view.stale_action_plan && (
        <p className="text-xs text-vg2-red mb-3"
           data-testid="pa-circle-stale">
          Il piano d'azione non è quello corrente: nessuna nuova
          selezione è consentita.
        </p>
      )}

      {view.role_needs.length === 0 && (
        <p className="text-sm text-vg2-muted"
           data-testid="pa-circle-empty">
          Nessun professionista esterno necessario in questa fase.
        </p>
      )}

      <ul className="space-y-4" data-testid="pa-circle-role-list">
        {view.role_needs.map((rn) => (
          <RoleNeedCard key={rn.role_need_ref} roleNeed={rn}
                        onSearch={() => setDrawer({ kind: "search", roleNeed: rn })}
                        onOwn={() => setDrawer({ kind: "own", roleNeed: rn })}
                        onTransition={async (member, target) => {
                          try {
                            await transitionMember({
                              paId, caseId, memberId: member.circle_member_id,
                              payload: {
                                target_status: target,
                                operation_id: newOperationId("tr"),
                              },
                            });
                            await load();
                          } catch (e) { setError(e); }
                        }}
                        onRemove={async (member) => {
                          if (!window.confirm(
                            `Rimuovere ${member.professional_display_name}? La cronologia sarà preservata.`
                          )) return;
                          try {
                            await removeMember({
                              paId, caseId, memberId: member.circle_member_id,
                              payload: { operation_id: newOperationId("rm") },
                            });
                            await load();
                          } catch (e) { setError(e); }
                        }} />
        ))}
      </ul>

      {view.ready_for_execution_gates.length > 0 && (
        <div className="mt-4 text-xs text-vg2-muted"
             data-testid="pa-circle-gates">
          Ruoli da coprire per il prossimo passo:
          <ul className="list-disc pl-5 mt-1">
            {view.ready_for_execution_gates.map((g, i) => (
              <li key={i}>{g}</li>
            ))}
          </ul>
        </div>
      )}

      {drawer?.kind === "search" && (
        <SearchDrawer paId={paId} caseId={caseId} roleNeed={drawer.roleNeed}
                      onClose={() => setDrawer(null)}
                      onSaved={async () => { setDrawer(null); await load(); }} />
      )}
      {drawer?.kind === "own" && (
        <OwnProfessionalDrawer paId={paId} caseId={caseId}
                               roleNeed={drawer.roleNeed} ownList={own.items}
                               onClose={() => setDrawer(null)}
                               onSaved={async () => { setDrawer(null); await load(); }} />
      )}
    </section>
  );
}


const COVERAGE_LABELS = {
  UNFILLED: "Non coperto",
  CANDIDATE_IDENTIFIED: "Candidato selezionato",
  FILLED: "Coperto",
  ACTIVE: "In corso",
  COMPLETED: "Completato",
};

const TIMING_LABELS = {
  NOW: "Ora", NEXT: "Prossimo passo", LATER: "Più avanti",
};


function RoleNeedCard({ roleNeed, onSearch, onOwn, onTransition, onRemove }) {
  return (
    <li className="border border-vg2-line rounded p-4"
        data-testid={`pa-role-need-${roleNeed.role_need_ref}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
        <div>
          <p className="font-medium">{roleNeed.role_label}</p>
          <p className="text-xs text-vg2-muted">{roleNeed.purpose}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-xs px-2 py-1 rounded bg-vg2-subtle"
                data-testid={`pa-role-timing-${roleNeed.role_need_ref}`}>
            {TIMING_LABELS[roleNeed.timing]}
          </span>
          <span className="text-xs px-2 py-1 rounded bg-vg2-subtle"
                data-testid={`pa-role-coverage-${roleNeed.role_need_ref}`}>
            {COVERAGE_LABELS[roleNeed.coverage]}
          </span>
        </div>
      </div>

      {roleNeed.members.length > 0 && (
        <ul className="space-y-2 mb-3"
            data-testid={`pa-role-members-${roleNeed.role_need_ref}`}>
          {roleNeed.members.map((m) => (
            <li key={m.circle_member_id}
                className="border border-vg2-line rounded p-3"
                data-testid={`pa-member-${m.circle_member_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">
                    {m.professional_display_name}
                    {m.is_from_stale_action_plan && (
                      <span className="ml-2 text-xs text-vg2-red">
                        · da piano precedente
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-vg2-muted">
                    {m.responsibility_summary}
                  </p>
                  <p className="text-xs text-vg2-muted">
                    Fonte: {m.professional_source_type === "PA_OWN_NETWORK"
                      ? "Rete personale"
                      : "Rete estesa VG"}
                  </p>
                </div>
                <span className="text-xs px-2 py-1 rounded bg-vg2-subtle"
                      data-testid={`pa-member-state-${m.circle_member_id}`}>
                  {m.membership_status_label}
                </span>
              </div>
              <MemberActions member={m} onTransition={onTransition}
                             onRemove={onRemove} />
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap gap-2">
        <button onClick={onOwn}
                data-testid={`pa-role-own-${roleNeed.role_need_ref}`}
                className="px-3 py-1 border border-vg2-line rounded text-sm hover:border-vg2-red hover:text-vg2-red">
          Usa un professionista che conosco
        </button>
        <button onClick={onSearch}
                data-testid={`pa-role-search-${roleNeed.role_need_ref}`}
                className="px-3 py-1 border border-vg2-line rounded text-sm hover:border-vg2-red hover:text-vg2-red">
          Cerca nella rete estesa VG
        </button>
      </div>
    </li>
  );
}


const OUTBOUND_TRANSITIONS = {
  SELECTED: [
    ["CONTACTED", "Segna contattato"],
    ["CONFIRMED", "Segna confermato"],
    ["DECLINED", "Ha declinato"],
  ],
  CONTACTED: [
    ["CONFIRMED", "Segna confermato"],
    ["DECLINED", "Ha declinato"],
  ],
  CONFIRMED: [
    ["ACTIVE", "Attiva collaborazione"],
    ["DECLINED", "Ha declinato"],
  ],
  ACTIVE: [
    ["COMPLETED", "Segna completato"],
  ],
};


function MemberActions({ member, onTransition, onRemove }) {
  const transitions = OUTBOUND_TRANSITIONS[member.membership_status] || [];
  if (!transitions.length &&
      ["DECLINED", "REPLACED", "REMOVED", "COMPLETED"]
        .includes(member.membership_status)) {
    return null;
  }
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {transitions.map(([target, label]) => (
        <button key={target}
                onClick={() => onTransition(member, target)}
                className="px-2 py-1 border border-vg2-line rounded text-xs hover:border-vg2-red hover:text-vg2-red"
                data-testid={`pa-member-${member.circle_member_id}-to-${target}`}>
          {label}
        </button>
      ))}
      <button onClick={() => onRemove(member)}
              className="px-2 py-1 border border-vg2-line rounded text-xs text-vg2-red hover:border-vg2-red"
              data-testid={`pa-member-${member.circle_member_id}-remove`}>
        Rimuovi
      </button>
    </div>
  );
}


function SearchDrawer({ paId, caseId, roleNeed, onClose, onSaved }) {
  const [items, setItems] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyRef, setBusyRef] = React.useState(null);
  const [responsibility, setResponsibility] = React.useState("");

  React.useEffect(() => {
    (async () => {
      try {
        const r = await searchNetwork({
          paId, caseId, roleNeedRef: roleNeed.role_need_ref,
        });
        setItems(r);
      } catch (e) { setError(e); }
    })();
  }, [paId, caseId, roleNeed]);

  const doSelect = async (proId) => {
    if (!responsibility.trim()) {
      window.alert("Indica il compito prima di selezionare.");
      return;
    }
    setBusyRef(proId);
    try {
      await selectMember({
        paId, caseId, payload: {
          role_need_ref: roleNeed.role_need_ref,
          professional_source_type: "VG_EXTENDED_NETWORK",
          professional_ref: proId,
          responsibility_summary: responsibility.trim(),
          operation_id: newOperationId("sel"),
        },
      });
      await onSaved();
    } catch (e) { setError(e); }
    finally { setBusyRef(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4 z-50"
         role="dialog" aria-modal="true"
         data-testid="pa-search-drawer">
      <div className="bg-white rounded max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <p className="font-display font-medium text-lg mb-2">
          Rete VG — {roleNeed.role_label}
        </p>
        <p className="text-xs text-vg2-muted mb-3">
          I risultati sono già filtrati per questo ruolo e per la zona
          dell'immobile.
        </p>
        <input value={responsibility}
               onChange={(e) => setResponsibility(e.target.value)}
               placeholder="Compito assegnato (obbligatorio)"
               data-testid="pa-search-responsibility"
               className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
        {error && (
          <p className="text-sm text-vg2-red mb-3"
             data-testid="pa-search-error">{error.message}</p>
        )}
        {!items && !error && (
          <p className="text-sm text-vg2-muted">Ricerca in corso…</p>
        )}
        {items && items.total === 0 && (
          <p className="text-sm text-vg2-muted"
             data-testid="pa-search-empty">
            {items.empty_reason}
          </p>
        )}
        {items && items.total > 0 && (
          <ul className="space-y-3" data-testid="pa-search-results">
            {items.items.map((p) => (
              <li key={p.professional_id}
                  className="border border-vg2-line rounded p-3"
                  data-testid={`pa-candidate-${p.professional_id}`}>
                <p className="font-medium">{p.display_name}</p>
                <p className="text-xs text-vg2-muted">
                  {p.role} · {p.availability === "AVAILABLE"
                    ? "Disponibilità operativa"
                    : "Disponibilità limitata"}
                </p>
                <ul className="mt-2 list-disc pl-5 text-xs text-vg2-ink">
                  {p.compatibility_reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
                <button onClick={() => doSelect(p.professional_id)}
                        disabled={busyRef === p.professional_id}
                        data-testid={`pa-select-${p.professional_id}`}
                        className="mt-2 px-3 py-1 bg-vg2-red text-white rounded text-sm hover:opacity-90 disabled:opacity-40">
                  {busyRef === p.professional_id ? "…" : "Seleziona per questo ruolo"}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex justify-end mt-4">
          <button onClick={onClose}
                  data-testid="pa-search-close"
                  className="px-3 py-1 border border-vg2-line rounded text-sm hover:bg-vg2-subtle">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}


function OwnProfessionalDrawer({ paId, caseId, roleNeed, ownList, onClose, onSaved }) {
  const [displayName, setDisplayName] = React.useState("");
  const [contact, setContact] = React.useState("");
  const [existingId, setExistingId] = React.useState("");
  const [responsibility, setResponsibility] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);

  const compatibleOwn = ownList.filter(
    (o) => o.professional_type === roleNeed.role,
  );

  const submit = async () => {
    if (!responsibility.trim()) {
      window.alert("Indica il compito prima di selezionare.");
      return;
    }
    setBusy(true); setError(null);
    try {
      let ref = existingId;
      if (!ref) {
        if (!displayName.trim() || !contact.trim()) {
          setError({ message: "Nome e contatto sono obbligatori." });
          setBusy(false); return;
        }
        const created = await createOwnProfessional({
          paId, caseId, payload: {
            display_name: displayName.trim(),
            professional_type: roleNeed.role,
            contact_method: contact.trim(),
            operation_id: newOperationId("own"),
          },
        });
        ref = created.own_professional_id;
      }
      await selectMember({
        paId, caseId, payload: {
          role_need_ref: roleNeed.role_need_ref,
          professional_source_type: "PA_OWN_NETWORK",
          professional_ref: ref,
          responsibility_summary: responsibility.trim(),
          operation_id: newOperationId("sel"),
        },
      });
      await onSaved();
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end md:items-center justify-center p-2 md:p-4 z-50"
         role="dialog" aria-modal="true"
         data-testid="pa-own-drawer">
      <div className="bg-white rounded max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <p className="font-display font-medium text-lg mb-3">
          Rete personale — {roleNeed.role_label}
        </p>
        {compatibleOwn.length > 0 && (
          <>
            <label className="block text-xs uppercase text-vg2-muted mb-1">
              Contatti già registrati per questo ruolo
            </label>
            <select value={existingId}
                    onChange={(e) => setExistingId(e.target.value)}
                    data-testid="pa-own-existing"
                    className="w-full mb-3 px-3 py-2 border border-vg2-line rounded">
              <option value="">Aggiungi nuovo contatto</option>
              {compatibleOwn.map((o) => (
                <option key={o.own_professional_id} value={o.own_professional_id}>
                  {o.display_name}
                </option>
              ))}
            </select>
          </>
        )}
        {!existingId && (
          <>
            <input value={displayName}
                   onChange={(e) => setDisplayName(e.target.value)}
                   placeholder="Nome del professionista"
                   data-testid="pa-own-name"
                   className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
            <input value={contact}
                   onChange={(e) => setContact(e.target.value)}
                   placeholder="Email o telefono"
                   data-testid="pa-own-contact"
                   className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
          </>
        )}
        <input value={responsibility}
               onChange={(e) => setResponsibility(e.target.value)}
               placeholder="Compito assegnato"
               data-testid="pa-own-responsibility"
               className="w-full mb-3 px-3 py-2 border border-vg2-line rounded" />
        {error && (
          <p className="text-sm text-vg2-red mb-3"
             data-testid="pa-own-error">{error.message}</p>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="pa-own-cancel"
                  className="px-3 py-1 border border-vg2-line rounded text-sm hover:bg-vg2-subtle">
            Annulla
          </button>
          <button onClick={submit} disabled={busy}
                  data-testid="pa-own-save"
                  className="px-3 py-1 bg-vg2-red text-white rounded text-sm disabled:opacity-40">
            {busy ? "…" : "Seleziona per questo ruolo"}
          </button>
        </div>
      </div>
    </div>
  );
}
